import api from './axiosApi'
import type {
  PaginatedWarehouseLookupEntriesResponse,
  WarehouseLookupBulkUpsertPayload,
  WarehouseLookupCreatePayload,
  WarehouseLookupEntry,
  WarehouseLookupEntryUsage,
  WarehouseLookupListParams,
  WarehouseLookupTableName,
  WarehouseLookupTableSummary,
  WarehouseLookupUpdatePayload,
} from '@/types/adminWarehouseLookups'

interface WarehouseLookupTablesResponse {
  tables: WarehouseLookupTableSummary[]
}

export async function listAdminWarehouseLookupTables(): Promise<
  WarehouseLookupTableSummary[]
> {
  const { data } = await api.get<WarehouseLookupTablesResponse>(
    '/admin/warehouse-lookups/tables'
  )
  return data.tables
}

export async function listAdminWarehouseLookupEntries(
  tableName: WarehouseLookupTableName,
  params: WarehouseLookupListParams = {}
): Promise<PaginatedWarehouseLookupEntriesResponse> {
  const { data } = await api.get<PaginatedWarehouseLookupEntriesResponse>(
    `/admin/warehouse-lookups/${tableName}`,
    {
      params: {
        ...(params.search ? { search: params.search } : {}),
        skip: params.skip ?? 0,
        limit: params.limit ?? 20,
      },
    }
  )
  return data
}

export async function createAdminWarehouseLookupEntry(
  tableName: WarehouseLookupTableName,
  payload: WarehouseLookupCreatePayload
): Promise<WarehouseLookupEntry> {
  const { data } = await api.post<WarehouseLookupEntry>(
    `/admin/warehouse-lookups/${tableName}`,
    payload
  )
  return data
}

export async function updateAdminWarehouseLookupEntry(
  tableName: WarehouseLookupTableName,
  id: string,
  payload: WarehouseLookupUpdatePayload
): Promise<WarehouseLookupEntry> {
  const { data } = await api.patch<WarehouseLookupEntry>(
    `/admin/warehouse-lookups/${tableName}/${id}`,
    payload
  )
  return data
}

export async function deleteAdminWarehouseLookupEntry(
  tableName: WarehouseLookupTableName,
  id: string
): Promise<void> {
  await api.delete(`/admin/warehouse-lookups/${tableName}/${id}`)
}

export async function getWarehouseLookupEntryUsage(
  tableName: WarehouseLookupTableName,
  lookupKey: string
): Promise<WarehouseLookupEntryUsage> {
  const { data } = await api.get<WarehouseLookupEntryUsage>(
    `/admin/warehouse-lookups/${tableName}/usage/${encodeURIComponent(lookupKey)}`
  )
  return data
}

export async function bulkUpsertAdminWarehouseLookupEntries(
  tableName: WarehouseLookupTableName,
  payload: WarehouseLookupBulkUpsertPayload
): Promise<WarehouseLookupEntry[]> {
  const { data } = await api.post<WarehouseLookupEntry[]>(
    `/admin/warehouse-lookups/${tableName}/bulk-upsert`,
    payload
  )
  return data
}
